import { useContext, useState } from 'react';

import { useTheme } from 'styled-components';

import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useModals, NotificationContext } from 'context';
import useOnInit from 'hooks/useOnInit';
import { SpecialistType } from 'services/specialist';
import {
  forwardUserToTC,
  getSpecialists,
} from 'services/specialist/specialist';

import { Button, Modal } from 'components';

import { ForwardProps, TherapyCompanion } from './types';
// eslint-disable-next-line import/order
import { YogaTheme } from 'types/others';

import * as S from './styles';

const Forward: React.FC<ForwardProps> = ({
  isOpen,
  modalKey,
  props: { endUser },
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { closeModal } = useModals();
  const theme = useTheme() as YogaTheme;
  const { setNotification } = useContext(NotificationContext);

  const [therapyCompanions, setTherapyCompanions] = useState<
    TherapyCompanion[]
  >([]);
  const [selectedTherapyCompanion, setSelectedTherapyCompanion] =
    useState<TherapyCompanion>();
  const [showConfirmation, setShowConfirmation] = useState<boolean>();

  const handleGetSpecialists = async () => {
    try {
      const { data } = await getSpecialists(
        true,
        SpecialistType.TC,
        'user_transferred_by_tc',
        endUser.cognitoID,
      );

      const specialists = data.map(specialist => {
        return {
          fullName: specialist.full_name,
          cognitoId: specialist.cognito_id,
        };
      });

      if (specialists.length) {
        setTherapyCompanions(specialists);
      } else {
        setNotification({
          message: intl.formatMessage({
            id: 'FORWARD.NOTIFICATION.ERROR',
          }),
          type: 'failure',
        });
        closeModal(modalKey);
      }
    } catch (e) {
      setNotification({
        message: intl.formatMessage({
          id: 'FORWARD.NOTIFICATION.ERROR',
        }),
        type: 'failure',
      });
      closeModal(modalKey);
    }
  };

  const handleTherapyCompanionForward = async () => {
    try {
      if (endUser.cognitoID && selectedTherapyCompanion?.cognitoId) {
        await forwardUserToTC(
          endUser.cognitoID,
          selectedTherapyCompanion?.cognitoId,
        );

        navigate('/care-provider/patient-list');

        setNotification({
          message: intl.formatMessage({
            id: 'FORWARD.NOTIFICATION.SUCCESS',
          }),
          type: 'success',
        });
      }
    } catch (e) {
      setNotification({
        message: intl.formatMessage({
          id: 'FORWARD.NOTIFICATION.ERROR',
        }),
        type: 'failure',
      });
    } finally {
      closeModal(modalKey);
    }
  };

  useOnInit(() => {
    handleGetSpecialists();
  });

  return (
    <Modal
      isOpen={isOpen}
      data-testid="forwardModal"
      closeModal={() => closeModal(modalKey)}
      overlayStyle={{ alignContent: 'center' }}
      contentStyle={{
        paddingBottom: `${theme.yoga.spacing.large}px`,
      }}
    >
      {!showConfirmation ? (
        <S.Container data-testid="forwardContainer">
          <S.Title data-testid="forwardModalTitle">
            <FormattedMessage id="FORWARD.MODAL.TITLE" />
          </S.Title>
          <S.TherapyCompanionSelect
            data-testid="forwardModalDropdown"
            label={intl.formatMessage({
              id: 'FORWARD.DROPDOWN.LABEL',
            })}
            onChange={(e: { label: string; value: string }) => {
              setSelectedTherapyCompanion({
                fullName: e.label,
                cognitoId: e.value,
              });
            }}
            options={therapyCompanions.map(tc => {
              return { label: tc.fullName, value: tc.cognitoId };
            })}
          />
          <Button
            data-testid="forwardModalButton"
            disabled={!selectedTherapyCompanion}
            onClick={() => setShowConfirmation(true)}
          >
            <FormattedMessage id="FORWARD.MODAL.BUTTON" />
          </Button>
        </S.Container>
      ) : (
        <S.Container data-testid="forwardContainerConfirmation">
          <S.Title>
            <FormattedMessage
              id="FORWARD.MODAL.CONFIRMATION.TITLE"
              values={{
                userName: endUser.fullName,
                specialistName: selectedTherapyCompanion?.fullName,
              }}
            />
          </S.Title>

          <S.ButtonGroup>
            <S.CancelButton
              type="button"
              data-testid="forwardModalCancelButton"
              onClick={() => closeModal(modalKey)}
            >
              <FormattedMessage id="FORWARD.MODAL.CANCEL" />
            </S.CancelButton>
            <Button
              data-testid="forwardModalConfirmButton"
              disabled={!selectedTherapyCompanion}
              onClick={handleTherapyCompanionForward}
            >
              <FormattedMessage id="FORWARD.MODAL.BUTTON" />
            </Button>
          </S.ButtonGroup>
        </S.Container>
      )}
    </Modal>
  );
};

export default Forward;
